import { useCallback, FC, useEffect, useMemo, useRef } from "react";
import { ItemListItemKind, ItemListItemRowData } from "@/components/item-list/types";
import { ReactMouseEvent } from "@/domains/react/types";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { MDSRowItem, MDSRowItemSlotName, MDSTextColor, MDSTextWeight, MDSSpacer } from "@/mds";
import { MDSText, MDSTextOverflow, MDSTextVariant } from "@/mds";
import { MDSIcon, MDSIconColor } from "@/mds";
import { MDSPanelWidths } from "@/mds/foundations";
import {
  ItemListRowDropdown,
  ItemListTitleIcon,
  ItemListItemSharedByBox,
  ItemListToggleIcon,
} from "@/components/item-list/rows/components";
import { ItemListItemLastActionBox } from "@/components/item-list/rows/components/item-list-item-last-action-box";
import { useMatch } from "react-router-dom";
import { appRoutes } from "@/app/router";

const DISABLE_NOTE_PREVIEW_ON_HOVER_ATTR = "data-disable-note-preview-on-hover";
const DISABLE_NOTE_PREVIEW_ON_HOVER_SELECTOR = `[${DISABLE_NOTE_PREVIEW_ON_HOVER_ATTR}="true"]`;

const shouldDisableNotePreviewOnHover = (target: EventTarget | null): boolean =>
  target instanceof Element && !!target.closest(DISABLE_NOTE_PREVIEW_ON_HOVER_SELECTOR);

export interface ItemListItemRowProps {
  data: ItemListItemRowData;
  noSeparator?: boolean;
}

export const ItemListItemRow: FC<ItemListItemRowProps> = observer(
  ({ data: { payload: item, mustClickOnRowTitle = false }, noSeparator }) => {
    const itemPreviewState = useMemo(() => item.createPreviewState?.(), [item]);

    const extraRowContent = useMemo(
      () => item.extraRow && { id: item.extraRow.id, content: item.extraRow.content?.() },
      [item]
    );

    const handleMouseEnter = item.listState?.isHoverable
      ? (event: ReactMouseEvent) => item.listState?.hoverItem(item.id, event)
      : undefined;

    const inSearchPage = !!useMatch(appRoutes.search({}).path);
    const isHovered = item.id === item.listState?.hoveredItemId;
    const isKeyboardHighlighted = item.id === item.listState?.keyboardHighlightedItemId;
    const isKeyboardPreviewed = item.id === item.listState?.keyboardPreviewedItemId;
    const isSelected = item.listState?.isSelected(item.id);
    const useControlledHover = !!item.listState;

    const itemChips = item.chips?.() || null;
    const hasChips = !!itemChips;

    const itemRef = useRef(item);
    itemRef.current = item;

    const handleItemClick = useCallback((event: ReactMouseEvent, canClickItem = true) => {
      event.stopPropagation();
      const item = itemRef.current;
      if (item.listState?.canSelect && event.shiftKey) {
        item.listState.selectItemsFromLastSelectionUntil(item.id);
        return;
      }

      // if (item.listState?.isSelectModeActive || (isMac() ? event.metaKey : event.ctrlKey)) {
      //   item.listState?.toggleItemSelection(item.id);
      //   return;
      // }

      if (canClickItem) {
        item.onClick({ itemId: item.id, event });
      }
    }, []);

    const handleRowClick = (event: ReactMouseEvent) => handleItemClick(event, !mustClickOnRowTitle);

    const handleTitleClick = (event: ReactMouseEvent) => handleItemClick(event);

    const shouldShowInteractiveHover = isHovered;
    const rightAlignedComponent = item.rightAlignedComponent?.(shouldShowInteractiveHover);
    const rightComponentTriggersActions = !!rightAlignedComponent;
    const showActions =
      (!item.listState?.isSelectModeActive || !isSelected) &&
      (rightComponentTriggersActions || (shouldShowInteractiveHover && !!item.dropdown));
    const showChipsAndShare = !rightAlignedComponent || !showActions || inSearchPage;

    // For note/collection rows (no page-specific rightAlignedComponent), the attribution
    // label stays visible on hover; only the date slot swaps to the row's "..." dropdown.
    // The rightAlignedComponent path (search/sessions) keeps its original RightSide behavior.
    const usesRightAlignedComponent = !!rightAlignedComponent;
    const showInlineDropdown =
      !usesRightAlignedComponent && showActions && !!item.dropdown && !inSearchPage;

    const preview =
      itemPreviewState?.previewUi && itemPreviewState?.anchorPreviewAtTheEnd ? (
        <div slot={MDSRowItemSlotName.LastAddon}>{itemPreviewState.previewUi}</div>
      ) : (
        itemPreviewState?.previewUi
      );

    const handleBottomRowMouseEnter = useCallback(
      (event: ReactMouseEvent<HTMLDivElement>) => {
        if (shouldDisableNotePreviewOnHover(event.target)) {
          return;
        }
        itemPreviewState?.handleMouseEnter?.();
      },
      [itemPreviewState]
    );

    const handleBottomRowMouseOver = useCallback(
      (event: ReactMouseEvent<HTMLDivElement>) => {
        if (!shouldDisableNotePreviewOnHover(event.target)) {
          return;
        }

        if (itemPreviewState?.hideImmediately) {
          itemPreviewState.hideImmediately();
        } else {
          itemPreviewState?.handleMouseLeave?.();
        }
      },
      [itemPreviewState]
    );

    const handleBottomRowMouseLeave = useCallback(
      (event: ReactMouseEvent<HTMLDivElement>) => {
        if (shouldDisableNotePreviewOnHover(event.relatedTarget)) {
          return;
        }
        itemPreviewState?.handleMouseLeave?.();
      },
      [itemPreviewState]
    );

    const canSelect = item.listState?.canSelect ?? false;

    const isSearchOrMessage = [ItemListItemKind.Search, ItemListItemKind.Message].includes(
      item.kind
    );

    useEffect(() => {
      itemPreviewState?.setKeyboardPreviewActive?.(isKeyboardPreviewed);

      return () => {
        itemPreviewState?.setKeyboardPreviewActive?.(false);
      };
    }, [isKeyboardPreviewed, itemPreviewState]);

    return (
      <MDSRowItem
        $isSelected={isSelected}
        $isHighlighted={isKeyboardHighlighted}
        $isHovered={isHovered}
        $enableCssHover={!useControlledHover}
        $isSpecial={item.isSpecial}
        $appearance={item.appearance}
        $mustClickOnRowTitle={mustClickOnRowTitle}
        noSeparator={noSeparator}
        className="mds-item-list-row" // Used in e2e tests
        onClick={handleRowClick}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseEnter}
      >
        <ItemListToggleIcon
          slot={MDSRowItemSlotName.LeftAddon}
          item={item}
          isSelected={isSelected}
          isHighlighted={canSelect && isHovered}
          itemRef={itemRef}
          handleItemClick={handleItemClick}
        />
        <ContentRow>
          <Content>
            <LabelContainer
              onClick={handleTitleClick}
              onMouseEnter={itemPreviewState?.handleMouseEnter}
              onMouseLeave={itemPreviewState?.handleMouseLeave}
            >
              <TitleText>
                {isSearchOrMessage && (
                  <MDSText
                    color={MDSTextColor.Emphasis}
                    variant={MDSTextVariant.TextS}
                    weight={MDSTextWeight.Smedium}
                    overflow={MDSTextOverflow.Ellipsis}
                  >
                    {item.label}
                  </MDSText>
                )}
                {!isSearchOrMessage && (
                  <MDSText
                    variant={MDSTextVariant.TextM}
                    weight={MDSTextWeight.Medium}
                    overflow={MDSTextOverflow.Ellipsis}
                  >
                    {item.label}
                  </MDSText>
                )}
              </TitleText>
              {showChipsAndShare && !!item.titleIcon && (
                <ItemListTitleIcon titleIcon={item.titleIcon} />
              )}
              {showChipsAndShare && (
                <ItemChipsContainerExpanded>{itemChips}</ItemChipsContainerExpanded>
              )}
            </LabelContainer>
          </Content>

          {/* A status (Recording/Uploading/Error/Awaiting Input) takes over the metadata
              column: it renders an icon + the full, non-truncated label and suppresses the
              attribution badge to make room (MEM-17213). Like the date, it hides on hover so
              the "..." dropdown overlay can take its place. */}
          {!usesRightAlignedComponent && !!item.status && (
            <SharedByAndDateContainer>
              {!showInlineDropdown && (
                <StatusSlot>
                  <MDSIcon
                    kind={item.status.iconKind}
                    size={12}
                    color={
                      item.status.tone === "danger" ? MDSIconColor.Danger : MDSIconColor.Emphasis
                    }
                  />
                  <MDSText
                    variant={MDSTextVariant.TextXs}
                    color={
                      item.status.tone === "danger" ? MDSTextColor.Danger : MDSTextColor.Emphasis
                    }
                  >
                    {item.status.label}
                  </MDSText>
                </StatusSlot>
              )}
            </SharedByAndDateContainer>
          )}

          {!usesRightAlignedComponent &&
            !item.status &&
            (!!item.lastAction || !!item.sharedBy || !!item.dateLabel) && (
              <SharedByAndDateContainer>
                {!!item.lastAction && (
                  <AttributionSlot>
                    <ItemListItemLastActionBox lastAction={item.lastAction} />
                  </AttributionSlot>
                )}
                {!item.lastAction && !!item.sharedBy && (
                  <AttributionSlot>
                    <ItemListItemSharedByBox
                      sharedBy={item.sharedBy}
                      sharedByVariant={item.sharedByVariant}
                    />
                  </AttributionSlot>
                )}
                {/* Date column is always mounted at a fixed width so the attribution column
                  keeps the same position whether or not the row is hovered. On hover the
                  date text is hidden; the "..." dropdown renders full-height in the RightAddon
                  slot below (where it keeps its rounded shape, unconstrained by this band). */}
                {!!item.dateLabel && (
                  <DateSlot>
                    {!showInlineDropdown && (
                      <StyledText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                        {item.dateLabel}
                      </StyledText>
                    )}
                  </DateSlot>
                )}
              </SharedByAndDateContainer>
            )}
        </ContentRow>

        {extraRowContent && (
          <BottomRow
            slot={MDSRowItemSlotName.BottomAddon}
            key={extraRowContent.id}
            onMouseEnter={handleBottomRowMouseEnter}
            onMouseOver={handleBottomRowMouseOver}
            onMouseLeave={handleBottomRowMouseLeave}
          >
            {!!hasChips && <ItemChipsContainerCollapsed>{itemChips}</ItemChipsContainerCollapsed>}
            <BottomRowContainer $limitWidth={inSearchPage && !showActions}>
              {extraRowContent.content}
            </BottomRowContainer>
          </BottomRow>
        )}

        {preview}

        {showActions && usesRightAlignedComponent && (
          <RightSide
            $expandWidth={!!rightAlignedComponent && !!item.dropdown}
            $topAlign={inSearchPage}
            slot={MDSRowItemSlotName.RightAddon}
          >
            {rightAlignedComponent}
            {item.dropdown && (
              <>
                <MDSSpacer />
                <ItemListRowDropdown itemId={item.id} dropdown={item.dropdown} />
              </>
            )}
          </RightSide>
        )}

        {/* Note/collection hover: the "..." dropdown is overlaid at the row's right edge
            (absolutely positioned) so it renders full-height/rounded without consuming
            layout width — keeping the attribution column from shifting on hover. */}
        {showInlineDropdown && (
          <InlineDropdownOverlay>
            <ItemListRowDropdown itemId={item.id} dropdown={item.dropdown!} />
          </InlineDropdownOverlay>
        )}
      </MDSRowItem>
    );
  }
);

const ContentRow = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  flex-shrink: 1;
`;

const BottomRow = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.text.secondary};
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const Content = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
`;

const LabelContainer = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  /*
   * Hug the content (title + chips) rather than grow to fill: this keeps the chips
   * tight against the title's ellipsis when the title truncates (4px gap), and lets
   * the free space collect after the chips in the Content flex parent.
   */
  flex: 0 1 auto;
  min-width: 0;
  gap: ${({ theme }) => theme.spacing["1"]};
`;

/*
 * The title shrinks and ellipsis-truncates when crowded, but does NOT grow — so a
 * short title stays its natural width and the chips hug it. It owns the ellipsis
 * directly (rather than relying on an ancestor's overflow:hidden, which hard-clips
 * without an ellipsis), so a long title shows "…" before the chips. The inner MDSText
 * fills it and inherits the truncation.
 */
const TitleText = styled.div`
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  & > * {
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
`;

const RightSide = styled.div<{ $expandWidth?: boolean; $topAlign?: boolean }>`
  align-items: flex-end;
  display: flex;
  margin-left: 10px;
  width: ${({ $expandWidth }) => ($expandWidth ? "auto" : "60px")};
  min-width: 60px;
  ${({ $topAlign }) =>
    $topAlign &&
    `
      align-self: flex-start;
      /* To center align to the row title */
      margin-top: -6px;
      /* Stable width so attachments are stable when hovering over the row */
      width: 140px;
  `}
`;

const StyledText = styled(MDSText)`
  text-align: right;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

/*
 * Fixed-geometry metadata column so avatars line up left and dates line up right
 * across every row (MEM-17172). It never shrinks, so the title is what truncates
 * when space runs out (MEM-17125).
 */
const SharedByAndDateContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;

  flex-shrink: 0;
  margin-left: 10px;

  /* Fixed height so swapping the taller "..." dropdown in on hover never grows the row. */
  height: 20px;

  @container mds-row-item (max-width: ${MDSPanelWidths.headerBreakpoint}px) {
    width: min-content;
  }
`;

/* Avatar is left-aligned; the name truncates within the fixed slot. */
const AttributionSlot = styled.div`
  display: flex;
  align-items: center;
  width: 136px;
  height: 100%;
  overflow: hidden;

  @container mds-row-item (max-width: ${MDSPanelWidths.headerBreakpoint}px) {
    width: auto;
  }
`;

/*
 * Right-aligned, fixed-width slot that holds the date or, on hover, the "..." dropdown
 * in its place (MEM-17186). Fixed width keeps dates aligned across rows (MEM-17172) and
 * keeps the attribution column from shifting when the date/dropdown swap. No overflow
 * clipping or fixed height here, so the 32px dropdown button keeps its rounded shape;
 * the parent's fixed height stops it from growing the row.
 */
const DateSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  width: 64px;
`;

/*
 * Status (icon + label) sizes to its content and never shrinks or ellipsis-truncates, so
 * the full message ("Uploading Error", "Awaiting Input", …) is always shown (MEM-17213).
 * Unlike DateSlot there is no fixed width — status rows hide the attribution column, which
 * frees the room. The title still owns the layout's flexible space and truncates first.
 */
const StatusSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  gap: ${({ theme }) => theme.spacing["1"]};
  white-space: nowrap;
`;

/*
 * Overlays the "..." dropdown at the right edge of the row on hover. Absolute positioning
 * (anchored to the position:relative StyledRowItem) keeps it out of the layout flow, so the
 * button renders full-height/rounded and the attribution column does not shift on hover.
 * right offset matches the row's horizontal padding (spacing/7). Top-aligned (not centered)
 * so the button lines up with the title/metadata row per the mock, which pads the button
 * 12px (spacing/3) from the top.
 */
const InlineDropdownOverlay = styled.div`
  position: absolute;
  right: ${({ theme }) => theme.spacing["7"]};
  top: ${({ theme }) => theme.spacing["3"]};
  display: flex;
  align-items: center;
`;

const ItemChipsContainerExpanded = styled.div`
  /* Chips keep their size; the title truncates instead of the chips */
  flex-shrink: 0;
  display: flex;
  align-items: center;

  @container mds-row-item (max-width: ${MDSPanelWidths.narrow}px) {
    display: none;
  }
`;

const BottomRowContainer = styled.div<{ $limitWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
  min-width: 0;

  ${({ $limitWidth }) =>
    $limitWidth &&
    `
    /* Stable width so attachments are stable when hovering over the row */
    max-width: calc(100% - 150px);
`}
`;

const ItemChipsContainerCollapsed = styled.div`
  @container mds-row-item (min-width: ${MDSPanelWidths.narrow}px) {
    display: none;
  }
`;
